import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import './AdminToolRegistry.css';

const STATE_COLORS  = { enabled: '#27ae60', beta: '#f39c12', disabled: '#e74c3c' };
const STATE_OPTIONS = [
    { value: 'enabled',  label: 'Включён' },
    { value: 'beta',     label: 'Бета' },
    { value: 'disabled', label: 'Выключен' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function EditableText({ value, onChange, placeholder, multiline = false, className = '' }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft]     = useState(value);
    useEffect(() => { setDraft(value); }, [value]);
    const commit = () => { setEditing(false); if (draft !== value) onChange(draft); };
    if (editing) {
        const props = {
            className: `trl__editable-input ${className}`,
            value: draft, autoFocus: true,
            onClick: e => e.stopPropagation(),
            onChange: e => setDraft(e.target.value),
            onBlur: commit,
            onKeyDown: e => {
                if (e.key === 'Escape') { setDraft(value); setEditing(false); }
                if (!multiline && e.key === 'Enter') commit();
            },
        };
        return multiline
            ? <textarea {...props} className={`trl__editable-area ${className}`} rows={2} />
            : <input {...props} />;
    }
    return (
        <span
            className={`trl__editable-text ${!value ? 'trl__editable-text--empty' : ''} ${className}`}
            onClick={e => { e.stopPropagation(); setEditing(true); }}
            title="Нажми чтобы редактировать"
        >
            {value || placeholder || '—'}
        </span>
    );
}

function StateSelect({ value, onChange }) {
    const color = STATE_COLORS[value] || '#999';
    return (
        <select
            className="trl__state-select"
            value={value || 'enabled'}
            onChange={e => onChange(e.target.value)}
            style={{ borderLeft: `3px solid ${color}` }}
        >
            {STATE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    );
}

function VisToggle({ value, onChange }) {
    return (
        <button
            className={`trl__toggle ${value ? 'trl__toggle--on' : 'trl__toggle--off'}`}
            onClick={() => onChange(!value)}
            title={value ? 'В тарифах' : 'Скрыт из тарифов'}
        >
            {value ? '👁' : '○'}
        </button>
    );
}

// ── Tool row (Layer 3) ────────────────────────────────────────────────────────

function ToolRow({ tool, planOrder, planLabels, onUpdate, depth = 2 }) {
    const upd = (f, v) => onUpdate(tool.key, f, v);
    const planColor = { free: '#27ae60', standard: '#2980b9', full: '#8e44ad' };
    return (
        <tr className="trl__tool-row" style={{ '--depth': depth }}>
            <td className="trl__cell trl__cell--icon">
                <EditableText value={tool.icon || ''} onChange={v => upd('icon', v)} placeholder="🔧" />
            </td>
            <td className="trl__cell trl__cell--title">
                <EditableText value={tool.title} onChange={v => upd('title', v)} placeholder="Название" />
                <code className="trl__tool-key">{tool.key}</code>
            </td>
            <td className="trl__cell trl__cell--desc">
                <EditableText value={tool.description} onChange={v => upd('description', v)} placeholder="Описание…" multiline />
            </td>
            <td className="trl__cell trl__cell--plan">
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {tool.min_plan && (
                        <span className="trl__plan-badge" style={{ background: planColor[tool.min_plan] || '#999' }}>
                            {planLabels?.[tool.min_plan] || tool.min_plan}
                        </span>
                    )}
                    <StateSelect value={tool.state || 'enabled'} onChange={v => upd('state', v)} />
                </div>
            </td>
            <td className="trl__cell trl__cell--order">
                <input
                    type="number"
                    className="trl__order-input"
                    value={tool.order ?? 0}
                    min="0"
                    onChange={e => upd('order', parseInt(e.target.value, 10) || 0)}
                />
            </td>
            <td className="trl__cell trl__cell--vis">
                <VisToggle value={!!tool.is_visible} onChange={v => upd('is_visible', v)} />
            </td>
        </tr>
    );
}

// ── Module row (Layer 2) ──────────────────────────────────────────────────────

function ModuleSection({ mod, planOrder, planLabels, onUpdateTool, onUpdateMod, collapsed, onToggle }) {
    const upd = (f, v) => onUpdateMod(mod.key, f, v);
    return (
        <>
            <tr className="trl__mod-row" onClick={onToggle} style={{ cursor: 'pointer' }}>
                <td className="trl__cell trl__cell--mod-label" colSpan={3}>
                    <span className="trl__expand-icon">{collapsed ? '▶' : '▼'}</span>
                    <EditableText
                        value={mod.label}
                        onChange={v => upd('label', v)}
                        placeholder="Модуль"
                        className="trl__mod-label-edit"
                    />
                    <code className="trl__cat-key">({mod.key})</code>
                    <span className="trl__cat-count">{mod.tools?.length || 0} инстр.</span>
                </td>
                <td className="trl__cell" onClick={e => e.stopPropagation()}>
                    <StateSelect value={mod.state} onChange={v => upd('state', v)} />
                </td>
                <td className="trl__cell trl__cell--order" onClick={e => e.stopPropagation()}>
                    <input
                        type="number"
                        className="trl__order-input"
                        value={mod.order ?? 0}
                        min="0"
                        onChange={e => upd('order', parseInt(e.target.value, 10) || 0)}
                    />
                </td>
                <td className="trl__cell trl__cell--vis" onClick={e => e.stopPropagation()}>
                    <VisToggle value={!!mod.is_visible} onChange={v => upd('is_visible', v)} />
                </td>
            </tr>
            {!collapsed && mod.tools?.map(tool => (
                <ToolRow
                    key={tool.key}
                    tool={tool}
                    planOrder={planOrder}
                    planLabels={planLabels}
                    onUpdate={onUpdateTool}
                    depth={3}
                />
            ))}
        </>
    );
}

// ── Section row (Layer 1) ─────────────────────────────────────────────────────

function SectionBlock({ sec, planOrder, planLabels, onUpdateTool, onUpdateSection, onUpdateModule, collapsedMods, onToggleMod, collapsed, onToggle }) {
    const updSec = (f, v) => onUpdateSection(sec.key, f, v);
    const totalTools = (sec.modules || []).reduce((n, m) => n + (m.tools?.length || 0), 0)
        + (sec.direct_tools?.length || 0);
    return (
        <div className="trl__section-block">
            <div className="trl__sec-header" onClick={onToggle}>
                <span className="trl__expand-icon">{collapsed ? '▶' : '▼'}</span>
                <span className="trl__sec-icon">{sec.icon}</span>
                <span className="trl__sec-label">{sec.label}</span>
                <code className="trl__cat-key">({sec.key})</code>
                <span className="trl__cat-count">{totalTools} инстр.</span>
                <div className="trl__sec-controls" onClick={e => e.stopPropagation()}>
                    <StateSelect value={sec.state} onChange={v => updSec('state', v)} />
                </div>
            </div>
            {!collapsed && (
                <table className="trl__table trl__table--nested">
                    <thead>
                        <tr>
                            <th className="trl__th-icon">Ик.</th>
                            <th>Название / ключ</th>
                            <th>Описание</th>
                            <th>Мин. тариф / Статус</th>
                            <th className="trl__th-order">№</th>
                            <th className="trl__th-vis">В тарифе</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Layer 2: Modules */}
                        {sec.modules?.map(mod => (
                            <ModuleSection
                                key={mod.key}
                                mod={mod}
                                planOrder={planOrder}
                                planLabels={planLabels}
                                onUpdateTool={onUpdateTool}
                                onUpdateMod={(key, f, v) => onUpdateModule(sec.key, key, f, v)}
                                collapsed={collapsedMods.has(mod.key)}
                                onToggle={() => onToggleMod(mod.key)}
                            />
                        ))}
                        {/* Direct tools (no module) */}
                        {sec.direct_tools?.map(tool => (
                            <ToolRow
                                key={tool.key}
                                tool={tool}
                                planOrder={planOrder}
                                planLabels={planLabels}
                                onUpdate={onUpdateTool}
                                depth={2}
                            />
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function AdminToolRegistry() {
    const { request } = useApi();
    const [data, setData]       = useState(null);
    const [sections, setSections] = useState([]);
    const [systemTools, setSystemTools] = useState([]);
    const [toolOverrides, setToolOverrides] = useState({});
    const [saving, setSaving]   = useState(false);
    const [saved, setSaved]     = useState(false);
    const [error, setError]     = useState('');
    const [collapsedSecs, setCollapsedSecs] = useState(new Set());
    const [collapsedMods, setCollapsedMods] = useState(new Set());

    const load = useCallback(async () => {
        try {
            const res = await request('/api/v1/admin/tool-registry');
            setData(res);
            setSections(JSON.parse(JSON.stringify(res.sections || [])));
            setSystemTools(JSON.parse(JSON.stringify(res.system_tools || [])));
            setToolOverrides({});
        } catch (e) {
            setError('Ошибка загрузки: ' + (e.message || e));
        }
    }, [request]);

    useEffect(() => { load(); }, [load]);

    // Update tool field anywhere in the tree
    const updateTool = (key, field, val) => {
        setSections(prev => prev.map(sec => ({
            ...sec,
            modules: sec.modules?.map(mod => ({
                ...mod,
                tools: mod.tools?.map(t => t.key === key ? { ...t, [field]: val } : t),
            })),
            direct_tools: sec.direct_tools?.map(t => t.key === key ? { ...t, [field]: val } : t),
        })));
        setToolOverrides(prev => ({
            ...prev,
            [key]: { ...(prev[key] || {}), [field]: val },
        }));
        setSaved(false);
    };

    const updateSection = (secKey, field, val) => {
        setSections(prev => prev.map(s => s.key === secKey ? { ...s, [field]: val } : s));
        setSaved(false);
    };

    const updateModule = (secKey, modKey, field, val) => {
        setSections(prev => prev.map(s =>
            s.key !== secKey ? s : {
                ...s,
                modules: s.modules?.map(m => m.key === modKey ? { ...m, [field]: val } : m),
            }
        ));
        setSaved(false);
    };

    const toggleSec = key => setCollapsedSecs(prev => {
        const s = new Set(prev);
        s.has(key) ? s.delete(key) : s.add(key);
        return s;
    });

    const toggleMod = key => setCollapsedMods(prev => {
        const s = new Set(prev);
        s.has(key) ? s.delete(key) : s.add(key);
        return s;
    });

    const handleSave = async () => {
        setSaving(true);
        setError('');
        try {
            // Собрать все оверрайды инструментов
            const allToolOverrides = {};
            const collectTools = tools => tools?.forEach(t => {
                const override = {
                    label: t.label, title: t.title, description: t.description,
                    icon: t.icon, group: t.group, order: t.order, is_visible: t.is_visible,
                };
                if (t.state && t.state !== 'enabled') {
                    override.state = t.state;
                }
                allToolOverrides[t.key] = override;
            });
            sections.forEach(sec => {
                sec.modules?.forEach(mod => collectTools(mod.tools));
                collectTools(sec.direct_tools);
            });
            collectTools(systemTools);

            const payload = {
                tools: allToolOverrides,
                sections: sections.map(sec => ({
                    key: sec.key,
                    state: sec.state,
                    modules: sec.modules?.map(mod => ({
                        key: mod.key,
                        label: mod.label,
                        order: mod.order,
                        is_visible: mod.is_visible,
                        state: mod.state,
                    })),
                })),
            };

            const res = await request('/api/v1/admin/tool-registry', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            setData(res);
            setSections(JSON.parse(JSON.stringify(res.sections || [])));
            setSystemTools(JSON.parse(JSON.stringify(res.system_tools || [])));
            setToolOverrides({});
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (e) {
            setError('Ошибка сохранения: ' + (e.message || e));
        } finally {
            setSaving(false);
        }
    };

    if (!data) return <div className="trl__loading">{error || 'Загрузка реестра инструментов…'}</div>;

    const planOrder  = data.plan_order  || [];
    const planLabels = data.plan_labels || {};

    return (
        <div className="trl">
            <div className="trl__header">
                <div>
                    <h2 className="trl__title">Реестр инструментов</h2>
                    <p className="trl__subtitle">
                        3 уровня: Раздел → Модуль → Инструмент. Нажми на текст — редактируй.
                        Состояние раздела/модуля влияет на сайдбар и API.
                    </p>
                </div>
                <div className="trl__actions">
                    {saved && <span className="trl__saved">Сохранено ✓</span>}
                    {error && <span className="trl__error">{error}</span>}
                    <button className="trl__save-btn" onClick={handleSave} disabled={saving}>
                        {saving ? 'Сохраняю…' : 'Сохранить'}
                    </button>
                </div>
            </div>

            {/* Layer 1 → 2 → 3 */}
            <div className="trl__sections">
                {sections.map(sec => (
                    <SectionBlock
                        key={sec.key}
                        sec={sec}
                        planOrder={planOrder}
                        planLabels={planLabels}
                        onUpdateTool={updateTool}
                        onUpdateSection={updateSection}
                        onUpdateModule={updateModule}
                        collapsedMods={collapsedMods}
                        onToggleMod={toggleMod}
                        collapsed={collapsedSecs.has(sec.key)}
                        onToggle={() => toggleSec(sec.key)}
                    />
                ))}
            </div>

            {/* System tools (price, marketplace_access) */}
            {systemTools.length > 0 && (
                <div className="trl__section-block trl__section-block--system">
                    <div className="trl__sec-header trl__sec-header--system">
                        <span className="trl__sec-icon">⚙️</span>
                        <span className="trl__sec-label">Системные инструменты</span>
                        <span className="trl__cat-count">{systemTools.length} инстр.</span>
                    </div>
                    <table className="trl__table trl__table--nested">
                        <thead>
                            <tr>
                                <th className="trl__th-icon">Ик.</th>
                                <th>Название / ключ</th>
                                <th>Описание</th>
                                <th>Мин. тариф</th>
                                <th className="trl__th-order">№</th>
                                <th className="trl__th-vis">В тарифе</th>
                            </tr>
                        </thead>
                        <tbody>
                            {systemTools.map(tool => (
                                <ToolRow
                                    key={tool.key}
                                    tool={tool}
                                    planOrder={planOrder}
                                    planLabels={planLabels}
                                    onUpdate={updateTool}
                                    depth={1}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="trl__footer">
                <button className="trl__save-btn" onClick={handleSave} disabled={saving}>
                    {saving ? 'Сохраняю…' : 'Сохранить изменения'}
                </button>
                <a href="/admin/plan-catalog" className="trl__link">→ Управление тарифами (1–6 планов)</a>
                <a href="/admin/plan-definitions" className="trl__link">→ Конструктор матрицы инструментов</a>
            </div>
        </div>
    );
}
